'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

function getIsMobile() {
  if (typeof window === 'undefined') return false;
  // Exclude iPad as it now has proper layout support
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Routes that genuinely require a desktop layout (column-view file manager,
// workflow DAG editors, dense admin tables, etc.). Everything else is treated
// as mobile-friendly so the homepage, /agent chat, /share/*, /s/* and auth
// pages all render on phones.
const MOBILE_BLOCKED_PREFIXES = ['/admin'];

function isMobileBlockedPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return MOBILE_BLOCKED_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

export function MobileDetect({ children }: { children: React.ReactNode }) {
  // Default to desktop (false) so SSR renders children: gating on a null
  // tri-state here stripped the ENTIRE page from the server HTML, which broke
  // native #anchor scrolling on first (uncached) visits — the anchor target
  // only existed after hydration, past the browser's fragment-scroll window.
  // Hydration matches (client also starts false); the block screen can only
  // appear after the UA effect runs, so it never flashes on desktop.
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsMobile(getIsMobile());
  }, [pathname]);

  if (isMobile && isMobileBlockedPath(pathname)) {
    return <MobilePrompt />;
  }

  return <>{children}</>;
}

function MobilePrompt() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-md text-center">
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/10 flex items-center justify-center">
            <svg className="w-10 h-10 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-3">
            暂不支持移动端访问
          </h1>
          <p className="text-slate-400 leading-relaxed">
            本系统暂时不支持移动端设备，请使用桌面浏览器访问以获得最佳体验。
          </p>
        </div>
        <div className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700/50">
          <p className="text-sm text-slate-500 mb-4">
            请在电脑浏览器中打开
          </p>
          <div className="flex justify-center gap-4">
            <div className="px-4 py-2 rounded-lg bg-slate-700/50 text-slate-400 text-sm">
              Chrome
            </div>
            <div className="px-4 py-2 rounded-lg bg-slate-700/50 text-slate-400 text-sm">
              Firefox
            </div>
            <div className="px-4 py-2 rounded-lg bg-slate-700/50 text-slate-400 text-sm">
              Safari
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
